import type { ValidatedPinUser } from 'washday-sdk';
import type { CashierPinRequestOptions } from '../../hooks/useRequestCashierPinValidation';
import { useTranslation } from 'react-i18next';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import { ActionIcon, Badge, Grid, Skeleton, Tooltip } from '@mantine/core';
import { useHistory } from 'react-router-dom';
import {
  IconBrandWhatsapp,
  IconCircleCheck,
  IconCurrencyDollar,
  IconEdit,
  IconPhoto,
  IconPrinter,
  IconReceiptDollar,
  IconShirt,
  IconTrash,
  IconUpload
} from '@tabler/icons-react';
import { useState } from 'react';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatMoneyString, getCustomerFullName, getWhatsappURLString } from '../../utils/strings';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';
import { modals } from '@mantine/modals';

import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import AmountDuePaymentModal from '../OrderModals/AmountDuePaymentModal';
import OrdersCardMoreActionsMenu from '../OrdersCardMoreActionsMenu';
import PrintTicketDrawer from '../PrintTicketDrawer';
import { buildOrderFolio, getOrderCardColorsByAmountDueAndStatus, isOrderPartiallyCollected } from '../../utils/ordersUtils';
import { getOrderAmountDue } from '@washday/utilities';
import { IStore } from '../../interfaces/Store';
import { getPiecesBreakdown, formatPiecesBreakdown } from '../../utils/helpers/getAllPieces';
import { useDisclosure } from '@mantine/hooks';
import useCanEditOrders from '../../hooks/useCanEditOrders';

const OrdersCardView = ({
  searchParamStoreId,
  onOrderCollected,
  onOrderCancelled,
  onSendEmailReceipt,
  onDownloadPNGTicket,
  showPaymentLinesModal,
  showOrderProductsModal,
  onOrderPayAndCollect,
  onMercadoPagoSelected,
  allowMercadoPago,
  orders = [],
  fetchNextPage = () => {},
  hasNextPage = false,
  onShowCreateOrderEvidenceModal,
  onShowOrderEvidenceModal,
  onAskForPin
}: {
  orders: any;
  fetchNextPage: any;
  hasNextPage: boolean;
  searchParamStoreId: string;
  onOrderPayAndCollect: ({
    orderId,
    sequence,
    storeId,
    paymentMethod,
    cashierBoxId,
    amount,
    paymentDate,
    collectWithAmountDue,
    pinUserId,
    attemptId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
    paymentMethod: string;
    cashierBoxId: string;
    amount: number;
    paymentDate: string;
    collectWithAmountDue: boolean;
    pinUserId?: string;
    pinValidationToken?: string;
    attemptId?: string | null;
  }) => Promise<void>;
  onMercadoPagoSelected?: (args: {
    order: IOrder;
    storeId: string;
    paymentMethod: string;
    cashierBoxId: string;
    amount: number;
    paymentDate: string;
    collectWithAmountDue: boolean;
    pinUserId?: string;
    pinValidationToken?: string;
  }) => void;
  allowMercadoPago?: boolean;
  onOrderCollected: ({
    orderId,
    sequence,
    storeId,
    pinUserId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
    pinUserId?: string;
    pinValidationToken?: string;
  }) => Promise<void>;
  onOrderCancelled: ({
    orderId,
    sequence,
    storeId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
  }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  onDownloadPNGTicket: (order: IOrder) => Promise<void>;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
  onShowCreateOrderEvidenceModal: (order: IOrder) => void;
  onShowOrderEvidenceModal: (order: IOrder) => void;
  onAskForPin: (options?: CashierPinRequestOptions) => Promise<ValidatedPinUser | null>;
}) => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'CleaningPage',
    'UpdateOrderForm',
    'ReportsPage',
    'OrdersHeaderSummary',
    'NewOrder'
  ]);
  const operationalReturnLabel = t('NewOrder:operationalReturns.badge');
  const history = useHistory();
  const canEditOrders = useCanEditOrders();
  const [openedPrintTicketDrawer, { open: openPrintTicketDrawer, close: closePrintTicketDrawer }] =
    useDisclosure(false);
  const [printTicketOrder, setPrintTicketOrder] = useState<IOrder | null>(null);

  const handleOpenPrintTicketDrawer = (order: IOrder) => {
    setPrintTicketOrder(order);
    openPrintTicketDrawer();
  };

  const handleClosePrintTicketDrawer = () => {
    closePrintTicketDrawer();
    setPrintTicketOrder(null);
  };

  const onCollectOrder = async ({
    order,
    hasAmountDue,
    amountDue
  }: {
    order: IOrder;
    hasAmountDue: boolean;
    amountDue: number;
  }) => {
    try {
      const sourceStoreId = typeof order.createdIn === 'string' ? order.createdIn : order.createdIn?._id || '';
      const pinUser = await onAskForPin({ storeId: sourceStoreId });
      return !hasAmountDue
        ? ConfirmationModal({
            title: t('Notifications:CRUD.collectedConfirmation'),
            onConfirm: () =>
              onOrderCollected({
                orderId: order._id || '',
                sequence: order.sequence || '',
                storeId: sourceStoreId,
                pinUserId: pinUser?._id,
                ...(pinUser?.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {})
              }),
            onCancel: () => {}
          })
        : modals.open({
            modalId: 'paidAndCollectedOrder',
            title: t('Notifications:CRUD.collectedConfirmation'),
            children: (
              <AmountDuePaymentModal
                order={order}
                close={() => modals.close('paidAndCollectedOrder')}
                storeId={sourceStoreId}
                onSubmit={onOrderPayAndCollect}
                amountDue={amountDue}
                pinUser={pinUser}
                allowMercadoPago={allowMercadoPago}
                onMercadoPagoSelected={({ paymentMethod, cashierBoxId, amount, paymentDate, collectWithAmountDue, pinUserId, pinValidationToken }) =>
                  onMercadoPagoSelected?.({
                    order,
                    paymentMethod,
                    cashierBoxId,
                    amount,
                    paymentDate,
                    collectWithAmountDue,
                    pinUserId,
                    pinValidationToken,
                    storeId: sourceStoreId
                  })
                }
              />
            )
          });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <PrintTicketDrawer
        opened={openedPrintTicketDrawer}
        onClose={handleClosePrintTicketDrawer}
        order={printTicketOrder}
      />
      {orders && orders.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={orders?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={
          <>
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
          </>
        }
        // endMessage={<Empty description={false} />}
      >
        <>
          <Grid gutter="md" pos={'relative'}>
            {orders?.map((order: IOrder) => {
              const amountDue = getOrderAmountDue(order);
              const hasAmountDue = amountDue > 0;
              const orderProducts = order?.products || [];
              const orderBuyAndGerProducts = order?.buyAndGetProducts || [];
              const allPieces = formatPiecesBreakdown(getPiecesBreakdown(orderProducts, orderBuyAndGerProducts), {
                measurementUnits: (order.createdIn as IStore)?.measurementUnits
              });
              const customColors = getOrderCardColorsByAmountDueAndStatus({
                amountDue,
                highlightOrderCardsByPaymentPending: (order.createdIn as Partial<IStore>)
                  ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                status: order.status
              });

              return (
                <Grid.Col key={order._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                  <CatalogCard
                    fields={[
                      {
                        label: t('FieldName:pieces'),
                        value: allPieces
                      },
                      {
                        label: t('FieldName:name'),
                        value: getCustomerFullName(order.customer.name, order.customer.lastName)
                      },
                      {
                        label: t('FieldName:total'),
                        value: formatMoneyString((order.total || 0).toFixed(2))
                      },
                      {
                        label: t('FieldName:amountDue'),
                        value: formatMoneyString(amountDue.toFixed(2))
                      },
                      {
                        label: t('FieldName:status'),
                        value: (
                          <>
                            {t(`FieldName:orderStatuses.${OrderStatuses[order.status.toUpperCase()]}`)}
                            {isOrderPartiallyCollected(order) && (
                              <Badge component="span" color="orange" size="xs" variant="light" ml={4}>
                                {t('FieldName:partialDeliveryBadge')}
                              </Badge>
                            )}
                          </>
                        )
                      },
                      {
                        label: t('FieldName:createdDate'),
                        value: moment(order.createdDate).format('DD/MM/YYYY HH:mm') || ''
                      },
                      {
                        label: t('FieldName:deliveryDate'),
                        value: moment(order.deliveryInfo?.date).format('DD/MM/YYYY') || ''
                      },
                      {
                        label: t('FieldName:notes'),
                        value: order.notes || '',
                        valueTooltip: order.notes
                      }
                    ]}
                    cardActionButtons={[
                      {
                        action: () => onShowCreateOrderEvidenceModal(order),
                        disabled: false,
                        Icon: <IconUpload />,
                        tooltip: t('OrdersPage:evidenceModal.uploadTooltip')
                      },
                      {
                        action: () => showPaymentLinesModal(order),
                        disabled: false,
                        Icon: <IconCurrencyDollar />,
                        tooltip: t('OrdersPage:paymentLinesModal.tooltip'),
                        testId: `order-action-payment-lines-${order._id}`
                      },
                      {
                        action: () => showOrderProductsModal(order),
                        disabled: false,
                        Icon: <IconShirt />,
                        tooltip: t('OrdersPage:productsModal.tooltip')
                      },
                      {
                        action: () =>
                          history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
                            storeID: searchParamStoreId
                          }),
                        disabled: !canEditOrders,
                        Icon: <IconEdit />,
                        tooltip: t('CommonButtons:edit')
                      },
                      {
                        action: () => onCollectOrder({ order, hasAmountDue, amountDue }),
                        disabled: false,
                        Icon: <IconCircleCheck />,
                        tooltip: 'Pedido Entregado',
                        testId: `order-action-mark-complete-${order._id}`
                      },
                      {
                        action: () =>
                          ConfirmationModal({
                            title: t('Notifications:CRUD.cancelConfirmation'),
                            onConfirm: () =>
                              onOrderCancelled({
                                orderId: order._id || '',
                                sequence: order.sequence || '',
                                storeId: order.createdIn?._id || ''
                              }),
                            onCancel: () => {}
                          }),
                        disabled: false,
                        Icon: <IconTrash />,
                        tooltip: 'Cancelar Pedido'
                      }
                    ]}
                    cardTitle={buildOrderFolio(order?.folioPrefix, order?.sequence) || ''}
                    cardTestId={`order-card-${order._id}`}
                    cardTitleExtra={
                      <>
                        {order.orderType === 'return' ? (
                          <Tooltip label={operationalReturnLabel} withinPortal={false}>
                            <Badge
                              color="blue"
                              mr={4}
                              size="xs"
                              style={{ flexShrink: 0 }}
                              variant="filled">
                              {operationalReturnLabel}
                            </Badge>
                          </Tooltip>
                        ) : null}
                        {(order?.evidence?.length ?? 0) > 0 && (
                          <Tooltip
                            withinPortal={false}
                            label={t('OrdersPage:evidenceModal.viewTooltip')}>
                            <ActionIcon
                              color={customColors.headerTextColor}
                              onClick={() => onShowOrderEvidenceModal(order)}
                              variant="transparent"
                              aria-label={t('OrdersPage:evidenceModal.viewTooltip')}>
                              <IconPhoto />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip withinPortal={false} label="Imprimir Ticket">
                          <ActionIcon
                            color={customColors.headerTextColor}
                            onClick={() => handleOpenPrintTicketDrawer(order)}
                            variant="transparent"
                            aria-label="Imprimir Ticket">
                            <IconPrinter />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          withinPortal={false}
                          label={
                            order.customer?.phone
                              ? t('CommonButtons:sendWhatsappMessage')
                              : t('CommonButtons:noCustomerPhone')
                          }>
                          <ActionIcon
                            color={customColors.headerTextColor || 'green'}
                            disabled={!order.customer.phone}
                            onClick={() => {
                              window.open(
                                getWhatsappURLString(
                                  order.customer?.phone,
                                  order.customer?.phoneCountryCode
                                ),
                                '_blank'
                              );
                            }}
                            variant="transparent"
                            aria-label={
                              order.customer?.phone
                                ? t('CommonButtons:sendWhatsappMessage')
                                : t('CommonButtons:noCustomerPhone')
                            }>
                            <IconBrandWhatsapp />
                          </ActionIcon>
                        </Tooltip>
                        <OrdersCardMoreActionsMenu order={order} customColors={customColors} />
                      </>
                    }
                    TitleIcon={IconReceiptDollar}
                    customColors={customColors}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </>
      </InfiniteScroll>
    </>
  );
};

export default OrdersCardView;
